import type { RJSFSchema, UiSchema } from '@rjsf/utils';

import ENDPOINTS from 'api/endpoints';
import type { IntegrationFormConfig } from '../types';
import { transformPhoneNumberPatternErrors } from '../utils/errorTransformers';
import { createPhoneNumbersDataSource } from '../utils/phoneNumbersDataSource';

const PHONE_NUMBER_WITH_COUNTRY_CODE_PATTERN = '^\\+[1-9]\\d{6,14}$';

const routingSchema: RJSFSchema = {
  required: ['sandbox', 'prerelease', 'live'],
  type: 'object',
  properties: {
    sandbox: {
      type: 'array',
      title: 'Sandbox',
      items: {
        minLength: 4,
        type: 'string',
        pattern: PHONE_NUMBER_WITH_COUNTRY_CODE_PATTERN,
      },
      default: [],
    },
    prerelease: {
      type: 'array',
      title: 'Pre-release',
      items: {
        minLength: 4,
        type: 'string',
        pattern: PHONE_NUMBER_WITH_COUNTRY_CODE_PATTERN,
      },
      default: [],
    },
    live: {
      type: 'array',
      title: 'Live',
      items: {
        minLength: 4,
        type: 'string',
        pattern: PHONE_NUMBER_WITH_COUNTRY_CODE_PATTERN,
      },
      default: [],
    },
  },
};

const numbersUiSchema: UiSchema = {
  sandbox: {
    'ui:widget': 'TagInputWidget',
    'ui:patternErrorMessage':
      'Please enter a valid phone number with country code (e.g. +441234567890)',
  },
  prerelease: {
    'ui:widget': 'TagInputWidget',
    'ui:patternErrorMessage':
      'Please enter a valid phone number with country code (e.g. +441234567890)',
  },
  live: {
    'ui:widget': 'TagInputWidget',
    'ui:patternErrorMessage':
      'Please enter a valid phone number with country code (e.g. +441234567890)',
  },
};

export const twilioFormConfig: IntegrationFormConfig = {
  dataSource: {
    path: `${ENDPOINTS.TELEPHONY_GATEWAY_PROXY}/twilio`,
    configPath: 'config',
  },
  steps: [
    {
      title: 'Step 1: Twilio account credentials',
      description:
        'Enter your Twilio Account credentials so PolyAI can automatically provision the SIP Trunk(s) needed to connect to your project.',
      schema: {
        required: ['api_key', 'api_secret', 'account_sid'],
        type: 'object',
        properties: {
          api_key: {
            minLength: 1,
            type: 'string',
            title: 'Api Key',
          },
          api_secret: {
            minLength: 1,
            type: 'string',
            title: 'API Secret',
            format: 'password',
          },
          account_sid: {
            minLength: 1,
            type: 'string',
            title: 'Account SID',
          },
        },
      },
    },
    {
      title: 'Step 2: Inbound routing',
      description: 'Configure routing rules for incoming calls.',
      dataSource: createPhoneNumbersDataSource('twilio'),
      schema: routingSchema,
      uiSchema: numbersUiSchema,
      transformErrors: errors => {
        const newErrors = transformPhoneNumberPatternErrors(errors);
        return newErrors;
      },
    },
  ],
  onSubmit: {
    path: `${ENDPOINTS.TELEPHONY_GATEWAY_PROXY}/twilio`,
    data: [
      'api_key',
      'api_secret',
      'account_sid',
      'sandbox',
      'prerelease',
      'live',
    ],
  },
};

export const updateTwilioFormConfig: IntegrationFormConfig = {
  dataSource: createPhoneNumbersDataSource('twilio'),
  steps: [
    {
      title: 'Step 1: Inbound routing',
      description: 'Configure routing rules for incoming calls.',
      schema: routingSchema,
      uiSchema: numbersUiSchema,
      transformErrors: errors => {
        const newErrors = transformPhoneNumberPatternErrors(errors);
        return newErrors;
      },
    },
  ],
  onSubmit: {
    path: `${ENDPOINTS.TELEPHONY_GATEWAY_PROXY}/twilio/routing`,
    data: ['sandbox', 'prerelease', 'live'],
  },
};
